import { useState } from "react";
import type React from "react";
import { Button } from "@excalidraw-ms/shared-ui";
import type { BackendCapabilityState } from "../auth/msalClient";

export const ACCESS_DENIED_HEADING = "Local-only mode";

interface BackendAccessDeniedNoticeProps {
  state?: Extract<BackendCapabilityState, "local-only" | "temporarily-unavailable">;
  layout?: "banner" | "page";
  onRetry?: () => void;
  onContinue?: () => void;
  onSignOut?: () => void;
  onGetFullAccess?: () => void;
}

export default function BackendAccessDeniedNotice({
  state = "local-only",
  layout = "banner",
  onRetry,
  onContinue,
  onSignOut,
  onGetFullAccess,
}: BackendAccessDeniedNoticeProps) {
  const [dismissed, setDismissed] = useState(false);
  if (layout === "banner" && dismissed) return null;

  const isLocalOnly = state === "local-only";
  const heading = isLocalOnly
    ? ACCESS_DENIED_HEADING
    : "Cloud features are temporarily unavailable";

  return (
    <div
      role={isLocalOnly ? "status" : "alert"}
      style={layout === "page" ? pageStyle : bannerStyle}
      data-testid="backend-access-denied-notice"
      data-state={state}
      data-layout={layout}
    >
      <span aria-hidden="true" style={{ fontSize: 16, marginRight: 10 }}>
        ℹ️
      </span>
      <span style={{ flex: 1, fontSize: 13, lineHeight: 1.4 }}>
        <strong>{heading}.</strong>{" "}
        {isLocalOnly ? (
          <>
            You can draw, import, export, and keep work in this browser.{" "}
            <button type="button" onClick={onGetFullAccess} style={linkStyle}>
              Get full access
            </button>{" "}
            for cloud saving, the gallery, sharing, feedback submission, and live collaboration.
          </>
        ) : (
          <>
            Local drawing still works in this browser. We couldn&rsquo;t connect to Excalidraw-MS or
            prepare your cloud storage.
          </>
        )}
        {layout === "page" && (
          <span style={actionRowStyle}>
            {!isLocalOnly && onRetry && (
              <Button variant="primary" onClick={onRetry}>
                Try again
              </Button>
            )}
            {onContinue && <Button onClick={onContinue}>Continue in local-only mode</Button>}
            {isLocalOnly && onSignOut && <Button onClick={onSignOut}>Sign out</Button>}
          </span>
        )}
      </span>
      {layout === "banner" && (
        <>
          {!isLocalOnly && onRetry && (
            <Button size="compact" onClick={onRetry} style={{ flexShrink: 0 }}>
              Try again
            </Button>
          )}
          <button
            type="button"
            onClick={() => setDismissed(true)}
            style={dismissStyle}
            aria-label={`Dismiss ${isLocalOnly ? "local-only mode" : "cloud unavailable"} notice`}
            data-testid="backend-access-denied-dismiss"
          >
            ✕
          </button>
        </>
      )}
    </div>
  );
}

const bannerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  padding: "10px 16px",
  borderTop: "1px solid #9ec5fe",
  background: "var(--exms-info-soft)",
  color: "var(--exms-info-text)",
  flexShrink: 0,
  fontFamily: "system-ui, sans-serif",
};

const linkStyle: React.CSSProperties = {
  border: 0,
  background: "none",
  padding: 0,
  font: "inherit",
  cursor: "pointer",
  color: "var(--exms-info-text)",
  fontWeight: 600,
  textDecoration: "underline",
};

const pageStyle: React.CSSProperties = {
  ...bannerStyle,
  maxWidth: 620,
  border: "1px solid #9ec5fe",
  borderRadius: 10,
  padding: "20px 22px",
};

const actionRowStyle: React.CSSProperties = {
  display: "flex",
  gap: 10,
  justifyContent: "center",
  marginTop: 18,
};

const dismissStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  fontSize: 13,
  cursor: "pointer",
  color: "var(--exms-info-text)",
  padding: "0 4px",
  marginLeft: 12,
  flexShrink: 0,
};
